import { Table } from '@/components/ui';
import { DataTablePagination, PanelInfo } from '@/components/custom';
import { IAccountingUser } from '@/interfaces';
import { TResponseData } from '@/hooks/httpRequest/accountingUsers/useRequestGetAccountingUsers';
import { TEditParams } from '@/hooks/forms/useFormAccountingUser';
import { TCallbackForm } from '@/types';
import DataTableBodySkeleton from './DataTableBodySkeleton';
import DataTableMobileSkeleton from './DataTableMobileSkeleton';
import DataTableBody from './DataTableBody';
import DataTableHeader from './DataTableHeader';
import DataTableMobile from './DataTableMobile';
import { UserX } from 'lucide-react';
import { DataTablePageSizeEnum } from '@/enums';

interface TProps {
    accountingUsers: IAccountingUser[];
    isLoading: boolean;
    isFetching: boolean;
    data: TResponseData;
    onDelete: (_id: string) => Promise<void>;
    onEdit: (params: TEditParams) => void;
    onSeeDetails: (accountingUser: IAccountingUser, callback?: TCallbackForm<IAccountingUser>) => void;
}

const DataTable = ({ accountingUsers, isLoading, isFetching, data, onDelete, onEdit, onSeeDetails }: TProps) => {

    // Initial loading
    if (isLoading) {
        return (
            <div className='space-y-5'>
                {/* Desktop Skeleton */}
                <div className='hidden md:block border rounded-md'>
                    <Table>
                        <DataTableHeader />
                        <DataTableBodySkeleton rows={DataTablePageSizeEnum._15} />
                    </Table>
                </div>
                {/* Mobile Skeleton */}
                <div className='block md:hidden'>
                    <DataTableMobileSkeleton count={DataTablePageSizeEnum._15} />
                </div>
            </div>
        );
    }

    // No data after loading
    if (!accountingUsers.length && !isFetching) {
        return (
            <PanelInfo
                title='Aucun utilisateur trouvé'
                description="Vous ne disposez d'aucun utilisateur correspondant à ces critères."
                className='py-10 md:py-26 border-none'
                icon={UserX} />
        );
    }

    return (
        <div className='space-y-5'>
            {/* Desktop Table View */}
            <div className='hidden md:block border rounded-md'>
                <Table>
                    <DataTableHeader />
                    {isFetching ? (
                        <DataTableBodySkeleton rows={Math.min(accountingUsers.length || 5, 10)} />
                    ) : (
                        <DataTableBody
                            accountingUsers={accountingUsers}
                            onDelete={onDelete}
                            onEdit={onEdit}
                            onSeeDetails={onSeeDetails} />
                    )}
                </Table>
            </div>

            {/* Mobile List View */}
            <div className='block md:hidden'>
                {isFetching ? (
                    <DataTableMobileSkeleton count={Math.min(accountingUsers.length || 5, 6)} />
                ) : (
                    <DataTableMobile
                        accountingUsers={accountingUsers}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onSeeDetails={onSeeDetails} />
                )}
            </div>

            {data?.pagination && <DataTablePagination pageCount={data.pagination.pageCount} />}
        </div>
    );

};

export default DataTable;

